import React, { useMemo } from 'react';

import { Popover, Tree, message } from 'antd';
import { FaBook } from 'react-icons/fa';
import { TreeItem } from 'src/pages/observers/components/PreviewMessageInput/styles';
import { type DataNode } from 'antd/es/tree';
import { IQueryFieldsItem } from 'src/interfaces/entities/observer';

// Twig: nomes com ponto (colunas de cruzamento) precisam de colchetes
const fieldAccess = (field: string) => (/^[a-zA-Z_]\w*$/.test(field) ? `.${field}` : `['${field}']`);

export default function Dictionary(
	{
		queryFields,
		reportQueries,
	}: { queryFields: IQueryFieldsItem[], reportQueries?: { key: string, fields: IQueryFieldsItem[] }[] }
) {

	const [ messageApi, contextHolder, ] = message.useMessage();

	const copyToClipboardWithFeedback = (text: string) => {
		navigator.clipboard.writeText(text);
		messageApi.info('Copiado para área de transferência!');
	};

	const copyItem = (key: string, text: string, label: string): DataNode => ({
		key,
		title: <TreeItem onClick={() => copyToClipboardWithFeedback(`{{${text}}}`)}>{label}</TreeItem>,
	});

	const observerTree = useMemo<DataNode[]>(() => [
		{
			key: '0',
			title: 'occurence',
			children: [
				{
					key: '0-0',
					title: 'affectedRows',
					children: [
						{
							key: '0-0-0',
							title: 'result',
							disabled: queryFields.length <= 0,
							children: queryFields.map(({ field, }, index) => ({
								key: `0-0-0-${index}`,
								title: (
									<TreeItem
										onClick={() => {
											const text = `occurence.affectedRows.result[0].${field}`;
											copyToClipboardWithFeedback(`{{${text}}}`);
										}}
									>
										{field}
									</TreeItem>
								),
							})),
						},
						{
							key: '0-0-1',
							title: (
								<TreeItem
									onClick={() => {
										const text = 'occurence.affectedRows.count';
										copyToClipboardWithFeedback(`{{${text}}}`);
									}}
								>
                                    count
								</TreeItem>
							),
						},
					],
				},
				{
					key: '0-1',
					title: 'affectedField',
					children: [
						{
							key: '0-1-0',
							title: 'result',
							disabled: queryFields.length <= 0,
							children: queryFields.map(({ field, }, index) => ({
								key: `0-1-0-${index}`,
								title: (
									<TreeItem
										onClick={() => {
											const text = `occurence.affectedField.result.${field}`;
											copyToClipboardWithFeedback(`{{${text}}}`);
										}}
									>
										{field}
									</TreeItem>
								),
							})),
						},
						{
							key: '0-1-1',
							title: (
								<TreeItem
									onClick={() => {
										const text = 'occurence.affectedField.count';
										copyToClipboardWithFeedback(`{{${text}}}`);
									}}
								>
                                    count
								</TreeItem>
							),
						},
					],
				},
				{
					key: '0-2',
					title: 'meta',
					children: [
						{
							key: '0-2-0',
							title: (
								<TreeItem
									onClick={() => {
										const text = 'occurence.meta.executionMS';
										copyToClipboardWithFeedback(`{{${text}}}`);
									}}
								>
                                    executionMS
								</TreeItem>
							),
						},
						{
							key: '0-2-1',
							title: (
								<TreeItem
									onClick={() => {
										const text = 'occurence.meta.totalRowsCount';
										copyToClipboardWithFeedback(`{{${text}}}`);
									}}
								>
                                    totalRowsCount
								</TreeItem>
							),
						},
						{
							key: '0-2-2',
							title: (
								<TreeItem
									onClick={() => {
										const text = 'occurence.meta.executionDate';
										copyToClipboardWithFeedback(`{{${text}}}`);
									}}
								>
                                    executionDate
								</TreeItem>
							),
						},
					],
				},
			],
		},
		{
			key: '1',
			title: 'observer',
			children: [
				{
					key: '1-0',
					title: (
						<TreeItem
							onClick={() => {
								const text = 'observer.observerId';
								copyToClipboardWithFeedback(`{{${text}}}`);
							}}
						>
                            observerId
						</TreeItem>
					),
				},
				{
					key: '1-1',
					title: (
						<TreeItem
							onClick={() => {
								const text = 'observer.name';
								copyToClipboardWithFeedback(`{{${text}}}`);
							}}
						>
                            name
						</TreeItem>
					),
				},
			],
		},
		{
			key: '2',
			title: 'severity',
			children: [
				{
					key: '2-0',
					title: (
						<TreeItem
							onClick={() => {
								const text = 'severity.name';
								copyToClipboardWithFeedback(`{{${text}}}`);
							}}
						>
                            name
						</TreeItem>
					),
				},
			],
		},
	], [ queryFields, ]);

	// Grupo de consultas: troca affectedRows/affectedField por queries.<key> e remove severity
	const tree = useMemo<DataNode[]>(() => {
		if (!reportQueries) return observerTree;
		const [ occurence, observer, ] = observerTree;
		return [
			{
				key: 'q',
				title: 'queries',
				children: reportQueries.map(({ key, fields, }, i) => ({
					key: `q-${i}`,
					title: key,
					children: [
						copyItem(`q-${i}-c`, `queries.${key}.count`, 'count'),
						{
							key: `q-${i}-r`,
							title: 'result[0]',
							disabled: !fields.length,
							children: fields.map(({ field, }, j) =>
								copyItem(`q-${i}-r-${j}`, `queries.${key}.result[0]${fieldAccess(field)}`, field)),
						},
					],
				})),
			},
			{ ...occurence, children: occurence.children?.filter((node) => node.key === '0-2'), },
			observer,
		];
	}, [ observerTree, reportQueries, ]);

	return (
		<Popover
			trigger={'click'}
			content={
				<Tree
					showLine
					treeData={tree}
				/>
			}
		>
			{contextHolder}
			<FaBook
				style={{ color: 'rgba(0,0,0,0.45)', cursor: 'help', }}
			/>
		</Popover>
	);
}
